import { cn } from '@/lib/utils';
import { useTrends } from '@/hooks/useClosedLoopAnalytics';
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import type { PerformanceTrend } from '@/types/closedLoopAnalytics';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface TrendIndicatorsProps {
  className?: string;
}

const METRIC_LABELS: Record<string, string> = {
  ctl: 'Fitness',
  atl: 'Load',
  tsb: 'Freshness',
  compliance: 'Compliance',
  rpe: 'RPE',
  volume: 'Volume',
  intensity: 'Intensity',
  quality: 'Quality',
};

function directionIcon(direction: string) {
  const d = direction.toLowerCase();
  if (d === 'improving' || d === 'up' || d === 'rising') return TrendingUp;
  if (d === 'declining' || d === 'down' || d === 'falling') return TrendingDown;
  return Minus;
}

function directionColor(metric: string, direction: string) {
  const d = direction.toLowerCase();
  // For RPE, "up" is bad (more perceived effort)
  if (metric === 'rpe') {
    if (d === 'improving' || d === 'down' || d === 'falling') return 'text-emerald-400';
    if (d === 'declining' || d === 'up' || d === 'rising') return 'text-amber-400';
    return 'text-muted-foreground';
  }
  // For TSB, we want it positive/rising pre-race
  if (d === 'improving' || d === 'up' || d === 'rising') return 'text-emerald-400';
  if (d === 'declining' || d === 'down' || d === 'falling') return 'text-amber-400';
  return 'text-muted-foreground';
}

function TrendCard({ trend }: { trend: PerformanceTrend }) {
  const Icon = directionIcon(trend.direction);
  const color = directionColor(trend.metric, trend.direction);
  const label = METRIC_LABELS[trend.metric] ?? trend.metric;
  const confidence = Math.round((trend.confidence ?? 0) * 100);

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', color)} />
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-mono tracking-wider uppercase text-[#A0A0A0]">
          {label}
        </span>
      </div>
      <span className={cn('text-[10px] font-mono font-bold tracking-wider uppercase', color)}>
        {trend.direction}
      </span>
      {confidence > 0 && (
        <span className="text-[9px] font-mono tabular-nums text-white/20">
          {confidence}%
        </span>
      )}
    </div>
  );
}

export function TrendIndicators({ className }: TrendIndicatorsProps) {
  const { data, isLoading } = useTrends();

  if (isLoading) {
    return (
      <div className={cn('p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">DETECTING TRENDS...</p>
      </div>
    );
  }

  if (!data || !data.trends || data.trends.length === 0) {
    return null;
  }

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
            PERFORMANCE TRENDS
          </span>
          <MetricInfoButton metricKey="performanceTrends" />
        </div>
        {data.overreaching_detected && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-400/10 border border-red-400/20">
            <AlertTriangle className="w-3 h-3 text-red-400" />
            <span className="text-[9px] font-mono text-red-400 uppercase tracking-wider">OVERREACHING</span>
          </div>
        )}
      </div>

      {/* Trend cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {data.trends.map((trend) => (
          <TrendCard key={trend.metric} trend={trend} />
        ))}
      </div>

      {/* Alerts */}
      {data.alerts && data.alerts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/[0.06]">
          <ul className="space-y-1">
            {data.alerts.slice(0, 3).map((alert, idx) => (
              <li key={idx} className="text-[10px] font-mono text-amber-400/80">
                <span className="text-amber-400">!</span> {alert}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
